import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["searchInput", "searchResults", "graphEndpoint", "graphFields", "graphResult", "executeBtn",
                     "compareResult", "probeInput", "probeResult", "dataExplorer", "dataContent"]

  connect() {
    this.debounceTimer = null
    this.selectedUserId = null

    // Close dropdown on click outside
    this._outsideClick = (e) => {
      if (!this.element.contains(e.target)) {
        this.searchResultsTarget.classList.add("hidden")
      }
    }
    document.addEventListener("click", this._outsideClick)

    // Auto-scroll terminal on new entries
    const log = document.getElementById("playground_log")
    if (log) {
      this._observer = new MutationObserver(() => {
        const container = log.parentElement
        if (container) container.scrollTop = container.scrollHeight
      })
      this._observer.observe(log, { childList: true })
    }
  }

  disconnect() {
    document.removeEventListener("click", this._outsideClick)
    this._observer?.disconnect()
  }

  // ─── User Search ─────────────────────────────

  search() {
    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.fetchUsers(), 300)
  }

  async fetchUsers() {
    const q = this.searchInputTarget.value.trim()
    const url = q.length >= 1
      ? `/manage/playground/search_users.json?q=${encodeURIComponent(q)}`
      : `/manage/playground/search_users.json`

    try {
      const res = await fetch(url)
      const data = await res.json()
      this.renderSearchResults(data.users)
    } catch (e) {
      console.error("[playground] search failed:", e)
    }
  }

  renderSearchResults(users) {
    const el = this.searchResultsTarget
    if (!users || !users.length) {
      el.innerHTML = `<div class="px-3 py-2 text-sm text-gray-400">No users found</div>`
      el.classList.remove("hidden")
      return
    }

    el.innerHTML = users.map(u => `
      <button class="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm flex items-center justify-between gap-2 transition-colors"
              data-action="click->playground#selectUser"
              data-user-id="${u.id}"
              data-user-email="${u.email}">
        <span class="truncate">
          <span class="font-medium text-gray-900 dark:text-white">${u.email}</span>
          <span class="text-gray-400 ml-1">${u.name !== u.email.split("@")[0] ? u.name : ""}</span>
        </span>
        ${u.connected
          ? `<span class="shrink-0 text-xs px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">Connected</span>`
          : u.meta_connect_id
            ? `<span class="shrink-0 text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500">${u.connect_status || "disconnected"}</span>`
            : ""}
      </button>
    `).join("")
    el.classList.remove("hidden")
  }

  selectUser(event) {
    const btn = event.currentTarget
    this.selectedUserId = btn.dataset.userId
    this.searchInputTarget.value = btn.dataset.userEmail
    this.searchResultsTarget.classList.add("hidden")

    // Load meta_connect_panel via Turbo Frame
    const frame = document.getElementById("playground_content")
    if (frame) {
      frame.src = `/manage/playground/meta_connect_panel?user_id=${this.selectedUserId}`
    }
  }

  // ─── Graph API ───────────────────────────────

  async executeGraphQuery(event) {
    if (!this.selectedUserId) return
    if (!this.hasGraphEndpointTarget) return

    const endpoint = this.graphEndpointTarget.value.trim()
    if (!endpoint) return

    const fields = this.hasGraphFieldsTarget ? this.graphFieldsTarget.value.trim() : ""
    const btn = event.currentTarget
    this.setLoading(btn, true)

    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch("/manage/playground/graph_query", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token, "Accept": "application/json" },
        body: JSON.stringify({ user_id: this.selectedUserId, endpoint, fields: fields || undefined })
      })

      const data = await res.json()
      if (this.hasGraphResultTarget) {
        const pre = this.graphResultTarget.querySelector("pre") || this.graphResultTarget
        pre.textContent = JSON.stringify(data.success ? data.data : data, null, 2)
        pre.className = `text-xs font-mono whitespace-pre-wrap ${data.success ? "text-gray-700 dark:text-gray-300" : "text-red-500 dark:text-red-400"}`
      }
    } catch (e) {
      if (this.hasGraphResultTarget) {
        const pre = this.graphResultTarget.querySelector("pre") || this.graphResultTarget
        pre.textContent = `Fetch error: ${e.message}`
        pre.className = "text-xs font-mono whitespace-pre-wrap text-red-500"
      }
    } finally {
      this.setLoading(btn, false)
    }
  }

  setQuickEndpoint(event) {
    if (this.hasGraphEndpointTarget) {
      this.graphEndpointTarget.value = event.currentTarget.dataset.endpoint
    }
  }

  // ─── Refresh ─────────────────────────────────

  async refreshResource(event) {
    if (!this.selectedUserId) return

    const btn = event.currentTarget
    const resourceType = btn.dataset.resourceType
    this.setLoading(btn, true)

    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      await fetch("/manage/playground/refresh_resource", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token, "Accept": "application/json" },
        body: JSON.stringify({ user_id: this.selectedUserId, resource_type: resourceType })
      })
    } catch (e) {
      console.error("[playground] refresh failed:", e)
    }

    // Keep loading state for 3s to indicate job was queued
    setTimeout(() => this.setLoading(btn, false), 3000)
  }

  // ─── Diagnostics ────────────────────────────

  async compareCounts(event) {
    if (!this.selectedUserId) return
    const btn = event.currentTarget
    this.setLoading(btn, true)

    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch("/manage/playground/compare_counts", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token, "Accept": "application/json" },
        body: JSON.stringify({ user_id: this.selectedUserId })
      })
      const data = await res.json()
      if (this.hasCompareResultTarget) {
        const d = data.data
        const missing = d.missing?.length || 0
        const color = missing > 0 ? "text-red-500" : "text-green-500"
        this.compareResultTarget.innerHTML = `<span class="${color}">Koala: ${d.koala ?? 'err'} | api_paged: ${d.api_paged ?? 'err'} | DB: ${d.local_db} | Missing: ${missing}</span>`
        if (d.missing?.length) {
          this.compareResultTarget.innerHTML += `<div class="mt-1 text-gray-400">${d.missing.map(a => `${a.id} (${a.name})`).join(', ')}</div>`
        }
      }
    } catch (e) {
      if (this.hasCompareResultTarget) this.compareResultTarget.textContent = `Error: ${e.message}`
    } finally {
      this.setLoading(btn, false)
    }
  }

  async probeAdAccount(event) {
    if (!this.selectedUserId || !this.hasProbeInputTarget) return
    const fbId = this.probeInputTarget.value.trim()
    if (!fbId) return

    const btn = event.currentTarget
    this.setLoading(btn, true)

    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch("/manage/playground/probe_ad_account", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token, "Accept": "application/json" },
        body: JSON.stringify({ user_id: this.selectedUserId, fb_ad_account_id: fbId })
      })
      const data = await res.json()
      if (this.hasProbeResultTarget) {
        this.probeResultTarget.classList.remove("hidden")
        const pre = this.probeResultTarget.querySelector("pre")
        pre.textContent = JSON.stringify(data, null, 2)
        pre.className = `text-xs font-mono whitespace-pre-wrap ${data.success ? "text-green-600 dark:text-green-400" : "text-red-500 dark:text-red-400"}`
      }
    } catch (e) {
      if (this.hasProbeResultTarget) {
        this.probeResultTarget.classList.remove("hidden")
        this.probeResultTarget.querySelector("pre").textContent = `Error: ${e.message}`
      }
    } finally {
      this.setLoading(btn, false)
    }
  }

  // ─── Data Explorer ─────────────────────────

  async loadDataExplorer(event) {
    if (!this.selectedUserId) return
    const btn = event.currentTarget
    this.setLoading(btn, true)

    try {
      const res = await fetch(`/manage/playground/data_explorer.json?user_id=${this.selectedUserId}`)
      const data = await res.json()
      if (this.hasDataContentTarget && data.success) {
        this.renderDataExplorer(data.data)
      }
    } catch (e) {
      if (this.hasDataContentTarget) {
        this.dataContentTarget.innerHTML = `<div class="card p-4 text-red-500 text-xs">Error: ${e.message}</div>`
      }
    } finally {
      this.setLoading(btn, false)
    }
  }

  renderDataExplorer(data) {
    const { businesses, ad_accounts, sync_requests } = data
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    let html = ""

    // Businesses
    html += `<div class="card p-4"><h4 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Businesses (${businesses.length})</h4><div class="space-y-2">`
    for (const b of businesses) {
      const badge = b.limited_access
        ? `<span class="text-xs px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">Limited Access</span>`
        : `<span class="text-xs px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">OK</span>`
      const clearBtn = b.limited_access
        ? `<button class="text-xs text-brand hover:underline ml-2" onclick="fetch('/manage/playground/clear_limited_access', { method:'POST', headers:{'Content-Type':'application/json','X-CSRF-Token':'${token}','Accept':'application/json'}, body:JSON.stringify({model_type:'business',fb_id:'${b.fb_business_id}'}) }).then(()=>this.textContent='Cleared!')">Clear flag</button>`
        : ""
      html += `<div class="flex items-center justify-between text-xs py-1.5 border-b border-gray-100 dark:border-gray-800">
        <div class="flex items-center gap-2">
          <span class="font-medium text-gray-900 dark:text-white">${b.name || 'Unnamed'}</span>
          <span class="font-mono text-gray-400">${b.fb_business_id}</span>
          ${badge}${clearBtn}
        </div>
        <span class="text-gray-400">${b.ad_account_count} accounts</span>
      </div>`
    }
    html += `</div></div>`

    // Ad Accounts
    html += `<div class="card p-4"><h4 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Ad Accounts (${ad_accounts.length})</h4><div class="space-y-1 max-h-64 overflow-y-auto">`
    for (const a of ad_accounts) {
      const statusBadge = a.limited_access
        ? `<span class="text-xs px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">Limited</span>`
        : a.manually_added
          ? `<span class="text-xs px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">Manual</span>`
          : ""
      html += `<div class="flex items-center justify-between text-xs py-1 border-b border-gray-100 dark:border-gray-800">
        <div class="flex items-center gap-2">
          <span class="text-gray-900 dark:text-white">${a.name || 'Unnamed'}</span>
          <span class="font-mono text-gray-400">${a.fb_ad_account_id}</span>
          ${statusBadge}
        </div>
        <span class="text-gray-400">status: ${a.account_status ?? '?'}</span>
      </div>`
    }
    html += `</div></div>`

    // Sync Requests
    html += `<div class="card p-4"><h4 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Recent Sync Requests (${sync_requests.length})</h4><div class="space-y-1 max-h-48 overflow-y-auto">`
    for (const sr of sync_requests) {
      const statusColor = sr.status === "completed" ? "text-green-600" : sr.status === "failed" ? "text-red-500" : "text-orange-500"
      html += `<div class="flex items-center justify-between text-xs py-1 border-b border-gray-100 dark:border-gray-800">
        <div class="flex items-center gap-2">
          <span class="${statusColor} font-medium">${sr.status}</span>
          <span class="text-gray-600 dark:text-gray-400">${sr.resource_type}</span>
          <span class="font-mono text-gray-400 text-[10px]">${sr.uuid?.slice(0, 8)}...</span>
        </div>
        <div class="flex items-center gap-2 text-gray-400">
          ${sr.stash_count > 0 ? `<span>${sr.stash_count} stashed</span>` : ""}
          <span>${new Date(sr.created_at).toLocaleTimeString()}</span>
        </div>
      </div>`
    }
    html += `</div></div>`

    this.dataContentTarget.innerHTML = html
  }

  // ─── Terminal ────────────────────────────────

  clearLog() {
    const log = document.getElementById("playground_log")
    if (log) log.innerHTML = ""
  }

  // ─── Loading State ───────────────────────────

  setLoading(btn, loading) {
    if (loading) {
      btn._originalHTML = btn.innerHTML
      btn.classList.add("opacity-50", "pointer-events-none", "inline-flex", "items-center", "gap-2")
      btn.innerHTML = `<svg class="w-4 h-4 animate-spin shrink-0" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg><span>Loading...</span>`
    } else {
      btn.classList.remove("opacity-50", "pointer-events-none")
      if (btn._originalHTML) btn.innerHTML = btn._originalHTML
    }
  }
}
